import type { Breadcrumb, Event } from '@sentry/react';

/**
 * Client-side Sentry filtering (passed to Sentry.init in src/index.tsx).
 *
 * IGNORED_ERRORS entries are matched by the SDK's inbound-filters integration against
 * the event message and the exception's "{type}: {value}" — strings match as substrings,
 * regexes with .test(). Matched events are dropped before sending and don't count
 * toward quota. Applies to captureException (logger.error), captureConsoleIntegration
 * and global handlers alike.
 */

// Fetch killed by the browser or the network (tab close, navigation, sleep, offline) — Chrome / Firefox / Safari.
// Shared with getApiErrorMessage's isNetworkError, so the agent-facing wording and this filter agree.
export const NETWORK_ERROR_MESSAGES = [
  'Failed to fetch',
  'NetworkError when attempting to fetch resource',
  'Load failed',
  'The network connection was lost',
];

// Non-actionable noise dropped before reaching Sentry.
export const IGNORED_ERRORS = [
  ...NETWORK_ERROR_MESSAGES,
  // Safari reports aborted requests with localized messages (SE/NO users)
  /^TypeError: (cancelled|annulleret|avbruten|avbrutt|abgebrochen)$/,
  // event-source-polyfill console.error()s every dropped SSE stream before reconnecting: Chrome's
  // "network error" for a body read cut mid-stream (ZRM-FRONTEND-6AR) and the polyfill's own heartbeat
  // timeout (ZRM-FRONTEND-8CA). Streams with `autoReconnect` treat both as reconnects and report only their
  // give-up. The meetings/log lists and the customer events timeline don't reconnect: their drop is logged
  // as "HTTP unknown: network error", which 'HTTP unknown:' already drops, so these lose their last trace.
  /^TypeError: network error$/,
  /^Error: No activity within \d+ milliseconds/,
  // Benign browser rendering-loop warning, never actionable
  'ResizeObserver loop limit exceeded',
  'ResizeObserver loop completed with undelivered notifications',
  // Outlook SafeLink crawler scanning emailed links
  'Object Not Found Matching Id',
  // Stale hashed assets requested after a deploy
  'ChunkLoadError',
  /Loading chunk [\d]+ failed/,
  /Loading CSS chunk/,
  // Intentional request cancellations, not real errors. Matched by name plus the
  // browser-specific messages, since the extracted type isn't always "AbortError"
  // (e.g. DOMException without a name, plain error-shaped objects)
  'AbortError',
  'The user aborted a request', // Chrome
  'The operation was aborted', // Firefox
  'Fetch is aborted', // Safari
  'signal is aborted without reason', // newer Chrome/Edge
  // Network-level failure surfaced through the HttpErrorResponse path (no status — request never completed)
  'HTTP unknown:',
  // Session/token expiry — backstop for the logger.ts 401 skip, catches all 401 variants
  // ("Token is expired", "Unable to parse authentication token") on any capture path
  /HTTP 401:/,
  // Auth0 SDK (@auth0/auth0-spa-js) noise — user closed the login popup, silent-auth
  // timeout (blocked third-party cookies / sleep), stale login callback state,
  // expired session requiring interactive re-login (same class as the HTTP 401 skip)
  /^Error: Popup closed$/,
  /^Error: Timeout$/,
  /^Error: Invalid state$/,
  /^Error: Login required$/,
  // LogDNA logger losing its connection — transport noise, not an app error
  'A connection-based error occurred that will not be retried',
  // Clipboard API called while the tab is unfocused (Dial Maker desktop)
  'Document is not focused',
  // Browser-extension content scripts injected into the page — extension APIs and
  // obfuscated (_0x...) code running in blob: URLs that denyUrls can't attribute
  'Invalid call to runtime.sendMessage()',
  'Error in getInitiatorsChain',
  /_0x[a-f0-9]+/,
];

// Errors originating from browser extensions, not our code (matched against the
// file URL of the top stack frame)
export const DENIED_URLS = [
  /^chrome-extension:\/\//,
  /^moz-extension:\/\//,
  /^safari(-web)?-extension:\/\//,
];

const withoutFragment = (url: string): string => url.split('#')[0];

// A link to the pay-now page carries the customer's pay-now link in the fragment. src/utils/takePayNowFragment.ts
// removes it before Sentry starts; these keep any URL fragment out of events and breadcrumbs as a second line.
export const dropUrlFragment = <T extends Event>(event: T): T => {
  if (event.request?.url) event.request.url = withoutFragment(event.request.url);

  return event;
};

export const dropNavigationFragments = (breadcrumb: Breadcrumb): Breadcrumb => {
  if (breadcrumb.category !== 'navigation' || !breadcrumb.data) return breadcrumb;

  const { from, to } = breadcrumb.data;

  return {
    ...breadcrumb,
    data: {
      ...breadcrumb.data,
      ...(typeof from === 'string' && { from: withoutFragment(from) }),
      ...(typeof to === 'string' && { to: withoutFragment(to) }),
    },
  };
};
